import type {
  CreateRoomResponse,
  ExchangeLinkResponse,
  FeedbackRequest,
  JoinRoomResponse,
  QuizSet,
  SocketTicketResponse,
} from "@quizshow/protocol";

async function apiRequest<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init.headers,
    },
  });
  const payload = (await response.json()) as T & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error ?? "Anfrage ist fehlgeschlagen.");
  }
  return payload;
}

export function createRoom(quiz: QuizSet): Promise<CreateRoomResponse> {
  return apiRequest("/api/rooms", { method: "POST", body: JSON.stringify({ quiz }) });
}

export async function uploadRoomMedia(roomId: string, token: string, mediaId: string, blob: Blob): Promise<void> {
  const response = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/media/${encodeURIComponent(mediaId)}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": blob.type },
    body: blob,
  });
  if (!response.ok) {
    const payload = (await response.json()) as { error?: string };
    throw new Error(payload.error ?? "Medium konnte nicht hochgeladen werden.");
  }
}

export async function downloadRoomMedia(roomId: string, token: string, mediaId: string): Promise<Blob> {
  const response = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/media/${encodeURIComponent(mediaId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("Medium konnte nicht geladen werden.");
  return response.blob();
}

export function submitFeedback(feedback: FeedbackRequest): Promise<{ accepted: boolean; configured: boolean }> {
  return apiRequest("/api/feedback", { method: "POST", body: JSON.stringify(feedback) });
}

export function joinRoom(roomCode: string, displayName: string): Promise<JoinRoomResponse> {
  return apiRequest("/api/rooms/join", {
    method: "POST",
    body: JSON.stringify({ roomCode: roomCode.toUpperCase(), displayName }),
  });
}

export function exchangeLink(linkId: string, secret: string): Promise<ExchangeLinkResponse> {
  return apiRequest(`/api/links/${encodeURIComponent(linkId)}/exchange`, {
    method: "POST",
    body: JSON.stringify({ secret }),
  });
}

export function issueSocketTicket(roomId: string, token: string, lastRevision: number): Promise<SocketTicketResponse> {
  return apiRequest(`/api/rooms/${encodeURIComponent(roomId)}/socket-ticket`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ lastRevision }),
  });
}
